import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { getLeadSource } from '@/lib/leadSource';
import { toast } from '@/components/ui/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Send, CheckCircle2 } from 'lucide-react';
import emailjs from 'emailjs-com';
import { z } from 'zod';

// Validation schema
const contactSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(50, 'First name must be less than 50 characters'),
  lastName: z.string().trim().min(1, 'Last name is required').max(50, 'Last name must be less than 50 characters'),
  email: z.string().trim().email('Invalid email address').max(255, 'Email must be less than 255 characters'),
  phone: z.string().trim().regex(/^[\d\s()+-]{10,20}$/, 'Invalid phone number format').min(10, 'Phone number must be at least 10 digits'),
  address: z.string().trim().max(200, 'Address must be less than 200 characters'),
  message: z.string().trim().max(2000, 'Message must be less than 2000 characters'),
  services: z.array(z.string()).min(1, 'Please select at least one service'),
  agreement: z.boolean().refine(val => val === true, 'You must agree to continue')
});

// EmailJS Configuration
const EMAILJS_SERVICE_ID = "service_6hcgklu";
const EMAILJS_BUSINESS_TEMPLATE_ID = "template_3a7dv0t";
const EMAILJS_AUTOREPLY_TEMPLATE_ID = "template_8r246dd";
const EMAILJS_PUBLIC_KEY = "82Zp1oXRrni9k3ssj";

// Initialize EmailJS
emailjs.init(EMAILJS_PUBLIC_KEY);

const ContactFormSimple = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    services: [] as string[],
    message: '',
    agreement: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleServiceChange = (service: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      services: checked 
        ? [...prev.services, service]
        : prev.services.filter(s => s !== service)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Validate form data
      const validatedData = contactSchema.parse(formData);
      
      await sendEmail(validatedData);

      // Send to enflow CRM (fire-and-forget — don't block on failure)
      fetch('/api/submit-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: validatedData.firstName,
          lastName: validatedData.lastName,
          email: validatedData.email,
          phone: validatedData.phone,
          address: validatedData.address,
          services: validatedData.services,
          message: validatedData.message,
          leadSource: getLeadSource(),
        }),
      }).catch(err => {
        if (import.meta.env.DEV) console.error('Enflow submission failed:', err);
      });

      toast({
        title: "Request Submitted",
        description: "We'll get back to you with a free estimate shortly!",
      });

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        services: [],
        message: '',
        agreement: false
      });

      setIsSubmitted(true);
    } catch (error) {
      if (error instanceof z.ZodError) {
        // Show validation errors
        const firstError = error.errors[0];
        toast({
          title: "Validation Error",
          description: firstError.message,
          variant: "destructive",
        });
      } else {
        // Log only error type, not user data
        if (import.meta.env.DEV) {
          console.error("Form submission error:", error instanceof Error ? error.message : 'Unknown error');
        }
        toast({
          title: "Submission Error",
          description: "There was a problem submitting your request. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const sendEmail = async (data: z.infer<typeof contactSchema>) => {
    try {
      const businessTemplateParams = {
        from_name: `${data.firstName} ${data.lastName}`.trim(),
        from_email: data.email,
        phone: data.phone,
        address: data.address,
        service: data.services.join(', '),
        message: data.message,
        to_email: "sales@venturapowerwashing.com"
      };

      const autoReplyTemplateParams = {
        customer_name: data.firstName,
        customer_email: data.email,
        customer_address: data.address,
        service: data.services.join(', '),
        business_phone: "(805) 464-7166",
        business_email: "sales@venturapowerwashing.com"
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_BUSINESS_TEMPLATE_ID,
        businessTemplateParams
      );

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_AUTOREPLY_TEMPLATE_ID,
        autoReplyTemplateParams
      );

      return true;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Email sending error:", error instanceof Error ? error.message : 'Unknown error');
      }
      throw error;
    }
  };
  
  if (isSubmitted) {
    return (
      <Card className="card-shadow">
        <CardContent className="p-6 md:p-10 flex flex-col items-center text-center">
          <img
            src="/logo/ventura-power-washing-vertical.png"
            alt="Ventura Power Washing"
            width="700"
            height="962"
            className="h-32 md:h-40 w-auto mb-6"
          />
          <CheckCircle2 className="h-10 w-10 text-ventura-green mb-4" />
          <h3 className="text-2xl font-bold mb-2">Thank You!</h3>
          <p className="text-gray-600 max-w-md mb-6">
            Your request has been submitted. We'll get back to you with a free estimate shortly!
          </p>
          <Button
            variant="outline"
            onClick={() => setIsSubmitted(false)}
          >
            Submit Another Request
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-shadow">
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name *</Label>
              <Input 
                id="firstName"
                name="firstName"
                required
                aria-required="true"
                placeholder="John"
                value={formData.firstName}
                onChange={handleChange}
                className="input-field"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input 
                id="lastName"
                name="lastName"
                required
                aria-required="true"
                placeholder="Doe"
                value={formData.lastName}
                onChange={handleChange}
                className="input-field"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input 
                id="email"
                name="email"
                type="email"
                required
                aria-required="true"
                placeholder="john@example.com"
                value={formData.email}
                onChange={handleChange}
                className="input-field"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input 
                id="phone"
                name="phone"
                required
                aria-required="true"
                placeholder="(123) 456-7890"
                value={formData.phone}
                onChange={handleChange}
                className="input-field"
              />
            </div>
          </div>
          
          <fieldset className="space-y-2" role="group" aria-labelledby="services-legend">
            <legend id="services-legend">Services Needed * <span className="text-sm font-normal">(Select all that apply)</span></legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 border rounded-md" role="group" aria-required="true" aria-describedby={formData.services.length === 0 ? "services-error" : undefined}>
              {[
                { id: 'pressure-washing', label: 'Pressure Washing' },
                { id: 'window-washing', label: 'Window Washing' },
                { id: 'carpet-cleaning', label: 'Carpet Cleaning' },
                { id: 'soft-wash', label: 'Soft Wash Exterior' },
                { id: 'gutter-cleaning', label: 'Gutter Cleaning' },
                { id: 'solar-panel-cleaning', label: 'Solar Panel Cleaning' },
                { id: 'dumpster-pad-cleaning', label: 'Dumpster Pad Cleaning' }
              ].map((service) => (
                <div key={service.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={service.id}
                    checked={formData.services.includes(service.id)}
                    onCheckedChange={(checked) => 
                      handleServiceChange(service.id, checked === true)
                    }
                  />
                  <Label htmlFor={service.id} className="text-sm font-normal cursor-pointer">
                    {service.label}
                  </Label>
                </div>
              ))}
            </div>
            {formData.services.length === 0 && (
              <p id="services-error" className="text-sm text-red-500" role="alert">Please select at least one service</p>
            )}
          </fieldset>
          
          <div className="space-y-2">
            <Label htmlFor="address">Service Address</Label>
            <Input 
              id="address"
              name="address"
              placeholder="123 Main St, City, CA 12345"
              value={formData.address}
              onChange={handleChange}
              className="input-field"
              aria-label="Service location address (optional)"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message">Additional Details</Label>
            <Textarea 
              id="message"
              name="message"
              placeholder="Tell us about your needs, property details, or preferred time for service..."
              value={formData.message}
              onChange={handleChange}
              className="input-field min-h-[120px]"
              aria-label="Additional details about your project"
            />
          </div>
          
          <div className="flex items-start space-x-3">
            <Checkbox 
              id="agreement"
              name="agreement"
              checked={formData.agreement}
              onCheckedChange={(checked) => 
                setFormData(prev => ({ ...prev, agreement: checked === true }))
              }
              required
              aria-required="true"
              className="mt-1"
            />
            <Label htmlFor="agreement" className="text-sm leading-relaxed">
              I agree to receive text messages and emails from Ventura Power Washing regarding my service inquiry and understand that message and data rates may apply. I can opt out at any time by replying STOP to text messages or unsubscribing from emails. *
            </Label>
          </div>
          
          
          <Button 
            type="submit" 
            className="button-primary w-full"
            disabled={isSubmitting || !formData.agreement || formData.services.length === 0}
            aria-label="Submit form to get free power washing estimate"
          >
            {isSubmitting ? 'Submitting...' : 'Get a Free Estimate Today!'}
            <Send className="ml-2 h-4 w-4" />
          </Button>
        </form>
        
      </CardContent>
    </Card>
  );
};

export default ContactFormSimple;